/**
 * Indicador de turno y energía.
 *
 * Incluye una cuenta atrás opcional que hoy no se usa: contra la IA no hay
 * prisa. El hueco existe porque el PvP asíncrono de la Fase 2 sí traerá una
 * fecha límite por turno desde el servidor, y entonces basta con pasarle
 * `secondsLeft`. Sin esa prop no pinta nada, así que no estorba.
 */

import { Hourglass, Zap } from 'lucide-react';

interface TurnTimerProps {
  turn: number;
  maxTurns: number;
  energy: number;
  maxEnergy: number;
  /** Segundos restantes del turno. Sin valor, no hay cuenta atrás. */
  secondsLeft?: number;
  turnSeconds?: number;
}

export function TurnTimer({
  turn,
  maxTurns,
  energy,
  maxEnergy,
  secondsLeft,
  turnSeconds = 60,
}: TurnTimerProps) {
  const isLastTurn = turn >= maxTurns;

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="flex flex-col leading-none">
        <span className="text-[9px] tracking-widest text-slate-400 uppercase">Turno</span>
        <span
          className={`text-base font-bold tabular-nums sm:text-lg ${
            isLastTurn ? 'text-rose-300' : 'text-white'
          }`}
        >
          {turn}
          <span className="text-slate-500">/{maxTurns}</span>
        </span>
      </div>

      {/* Una marca por punto de energía: se ve de un vistazo lo que queda sin
          tener que leer un número. */}
      <div className="flex items-center gap-1">
        <Zap className="h-4 w-4 text-sky-300" aria-hidden />
        <div className="flex gap-0.5">
          {Array.from({ length: Math.max(maxEnergy, energy) }, (_, index) => (
            <span
              key={index}
              className={`h-3 w-1.5 rounded-xs sm:h-4 sm:w-2 ${
                index < energy ? 'bg-sky-400 shadow-[0_0_6px_rgba(56,189,248,0.8)]' : 'bg-slate-700'
              }`}
            />
          ))}
        </div>
        <span className="ml-0.5 text-xs font-semibold tabular-nums text-sky-200 sm:text-sm">
          {energy}
        </span>
      </div>

      {secondsLeft !== undefined ? (
        <div className="flex items-center gap-1">
          <Hourglass className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-700">
            <div
              className={`h-full transition-[width] duration-1000 ease-linear ${
                secondsLeft <= 10 ? 'bg-rose-400' : 'bg-emerald-400'
              }`}
              style={{ width: `${Math.max(0, Math.min(100, (secondsLeft / turnSeconds) * 100))}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
